import { STATUS_ENUM } from 'constants/report';

import React, { useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, Modal, Tooltip, useTheme } from '@mui/material';
import MaterialButton from 'components/MaterialButton';
import { UpdateReportQa } from 'hooks/useUpdateReportQa';
import { useSelector } from 'react-redux';
import selectGgEnvironment from 'selectors/ggEnvironment';
import { isAzure } from 'utils/environmentUtils';
import { hasCustomClassDbProducts, isSuccessful } from 'utils/reportUtils';

import CustomClassDbModal from './CustomClassDbModal';
import CloneReportMenuItem from './items/CloneReportMenuItem';
import CustomClassDbReportMenuItem from './items/CustomClassDbReportMenuItem';
import DisapproveReportMenuItem from './items/DisapproveReportMenuItem';
import RejectReportMenuItem from './items/RejectReportMenuItem';
import {
  CloneReportV2MenuItem,
  UpdateReportV2MenuItem,
} from './items/ReportRequestV2MenuItems';
import UnRejectReportMenuItem from './items/UnRejectReportMenuItem';
import UpdateReportMenuItem from './items/UpdateReportMenuItem';
import ViewJobLogMenuItem from './items/ViewJobLogMenuItem';
import ViewReportMenuItem from './items/ViewReportMenuItem';
import useStyles from './useStyles';

interface Props {
  updateReportJobStatus: UpdateReportQa;
  report: reports.AllReportsQueryRow;
}

// Approval is intentionally not offered here: reports are approved from the
// QA preview, after the reviewer has seen them as the client will.
export const getMenuAvailability = (
  report: reports.AllReportsQueryRow,
  azure: boolean,
) => {
  // Observable writes `mix_and_match: []` on plain optimizations, so only a
  // non-empty list marks a real Mix and Match report.
  const mixAndMatch = (report.params as Record<string, unknown> | null)
    ?.mix_and_match;
  const isMixAndMatch = Array.isArray(mixAndMatch) && mixAndMatch.length > 0;
  const canViewJobLog = Boolean(report.jobId) && !azure;
  const isMenuDisabled =
    (report.reportType === 'update_products' || isMixAndMatch) &&
    report.reportStatus !== STATUS_ENUM.SUCCESS &&
    !canViewJobLog;
  let disabledTooltip = '';
  if (isMenuDisabled) {
    disabledTooltip = isMixAndMatch
      ? 'Options become available once this report succeeds, or once its job log is available.'
      : 'Options become available for Update Products reports once they succeed, or once their job log is available.';
  }
  return { isMixAndMatch, canViewJobLog, isMenuDisabled, disabledTooltip };
};

const ReportViewerMenu: React.FC<Props> = ({
  updateReportJobStatus,
  report,
}) => {
  const res = [report];
  const classes = useStyles(useTheme());
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [showCustomClassDbModal, setShowCustomClassDbModal] =
    useState<boolean>(false);
  const [showUpdateReportDisabledModal, setShowUpdateReportDisabledModal] =
    useState<boolean>(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const environment = useSelector((state) => selectGgEnvironment(state));

  const { isMixAndMatch, canViewJobLog, isMenuDisabled, disabledTooltip } =
    getMenuAvailability(report, isAzure(environment));

  return (
    <div>
      <Tooltip title={disabledTooltip} placement="top">
        <span>
          <IconButton
            aria-label="more actions"
            id="long-button"
            aria-controls={open ? 'long-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleClick}
            disabled={isMenuDisabled}
          >
            <MoreVertIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {/* Allow cloning except for update_products and mix_and_match reports */}
        {res[0].reportType !== 'update_products' && !isMixAndMatch && (
          <CloneReportMenuItem handleClose={handleClose} report={res[0]} />
        )}

        {/* V2 can also clone mix & match reports */}
        {res[0].reportType !== 'update_products' && (
          <CloneReportV2MenuItem handleClose={handleClose} report={res[0]} />
        )}

        {
          /* If successful, allow previewing as the client sees it */
          isSuccessful(res[0]) && (
            <ViewReportMenuItem handleClose={handleClose} report={res[0]} />
          )
        }

        {
          /* If successful and not reviewed, allow rejecting */
          isSuccessful(res[0]) && res[0].passedQa === null && (
            <RejectReportMenuItem
              handleClose={handleClose}
              report={res[0]}
              updateReportJobStatus={updateReportJobStatus}
            />
          )
        }

        {
          /* If successful and rejected, allow un-rejecting */
          isSuccessful(res[0]) && res[0].passedQa === false && (
            <UnRejectReportMenuItem
              handleClose={handleClose}
              report={res[0]}
              updateReportJobStatus={updateReportJobStatus}
            />
          )
        }

        {
          /* If approved , allow disappoving */
          res[0].passedQa && res[0].noOfProjectsAddedTo === 0 && (
            <DisapproveReportMenuItem
              handleClose={handleClose}
              report={res[0]}
              updateReportJobStatus={updateReportJobStatus}
            />
          )
        }

        {
          /* If successful and not an optimization report, allow running update products*/
          isSuccessful(res[0]) && res[0].reportType !== 'optimization' && (
            <UpdateReportMenuItem
              handleClose={handleClose}
              report={res[0]}
              setShowUpdateReportDisabledModal={
                setShowUpdateReportDisabledModal
              }
            />
          )
        }

        {/* Super admins only, like the original update flow */}
        {isSuccessful(res[0]) && res[0].reportType !== 'optimization' && (
          <UpdateReportV2MenuItem handleClose={handleClose} report={res[0]} />
        )}

        {
          /* If has customClassDb , allow modal */
          hasCustomClassDbProducts(res[0]) && (
            <CustomClassDbReportMenuItem
              handleClose={handleClose}
              report={res[0]}
              setShowCustomClassDbModal={setShowCustomClassDbModal}
            />
          )
        }

        {
          /* If has jobId, allow viewing job log */
          canViewJobLog && (
            <ViewJobLogMenuItem handleClose={handleClose} report={res[0]} />
          )
        }
      </Menu>

      {/* Modals */}
      <CustomClassDbModal
        show={showCustomClassDbModal}
        close={() => setShowCustomClassDbModal(false)}
        report={res[0]}
      />

      {/* Update Report Disabled Modal */}
      <Modal
        open={showUpdateReportDisabledModal}
        onClose={() => setShowUpdateReportDisabledModal(false)}
        className={classes.modal}
      >
        <div className={classes.modalContainer}>
          <h3 style={{ marginBottom: '1rem' }}>
            Update Report Access Temporarily Disabled
          </h3>
          <p style={{ marginBottom: '1.5rem', lineHeight: '1.5' }}>
            Update Product has a narrow use case, so access is temporarily
            disabled for review. Please submit a JIRA ticket to request it.
          </p>
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}
          >
            <MaterialButton
              variant="outlined"
              soft
              onClick={() => setShowUpdateReportDisabledModal(false)}
            >
              Close
            </MaterialButton>
            <MaterialButton
              variant="outlined"
              soft
              teal
              onClick={() => {
                window.open(
                  'https://adlm.nielseniq.com/jira/servicedesk/customer/portal/52/create/1244',
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
            >
              Submit JIRA Ticket
            </MaterialButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

ReportViewerMenu.displayName = 'ReportViewerMenu';

export default ReportViewerMenu;
